import { useEffect, useRef, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Zap, MessageSquare, Calendar, DollarSign, ShieldCheck, TrendingUp, Check } from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";

const LOGO_URL =
  "/logo-brand.png";

function currency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

export default function FlyerPage() {
  const [s, setS] = useState(null);
  const [avgTicket, setAvgTicket] = useState(450);
  const [weeklyDeclines, setWeeklyDeclines] = useState(40);
  const [conversion, setConversion] = useState(15);
  const [saveState, setSaveState] = useState("idle"); // 'idle' | 'saving' | 'saved'
  const saveTimer = useRef(null);
  const savedTimer = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const monthlyDeclines = weeklyDeclines * 4;
  const monthlyRecovered = Math.round(monthlyDeclines * (conversion / 100));
  const grossMonthly = monthlyRecovered * avgTicket;
  const yearlyGross = grossMonthly * 12;

  // Debounced save-to-server. Applies UI update immediately; server call fires 600ms
  // after the user stops typing, then toasts + shows a "Saved" pill for a moment.
  const saveOwner = (patch) => {
    if (!s) return;
    const next = { ...s, ...patch };
    setS(next);
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put("/settings", next);
        setSaveState("saved");
        toast.success("Contact details saved");
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSaveState("idle"), 2000);
      } catch (_) {
        setSaveState("idle");
        toast.error("Couldn't save — check your connection");
      }
    }, 600);
  };

  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Marketing</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Dealer Flyer</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            A one-page hand-out for the dealer principal. Adjust the ROI math for their store size, then hit Print
            (or Save as PDF).
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-flyer-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print Flyer
        </Button>
      </div>

      {/* Controls */}
      <div className="mb-2 no-print flex items-center gap-2 text-xs">
        <span className="font-mono-label text-muted-foreground">Your contact info auto-saves as you type.</span>
        {saveState === "saving" && (
          <span className="text-muted-foreground animate-pulse" data-testid="flyer-save-state">Saving…</span>
        )}
        {saveState === "saved" && (
          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid="flyer-save-state">
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Saved
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 max-w-4xl no-print">
        <div>
          <Label htmlFor="wd" className="font-mono-label">Declines / week</Label>
          <Input
            id="wd"
            type="number"
            value={weeklyDeclines}
            onChange={(e) => setWeeklyDeclines(Number(e.target.value))}
            className="mt-1.5"
            data-testid="flyer-declines"
          />
        </div>
        <div>
          <Label htmlFor="cv" className="font-mono-label">Conversion %</Label>
          <Input
            id="cv"
            type="number"
            value={conversion}
            onChange={(e) => setConversion(Number(e.target.value))}
            className="mt-1.5"
            data-testid="flyer-conversion"
          />
        </div>
        <div>
          <Label htmlFor="tk" className="font-mono-label">Avg RO ticket ($)</Label>
          <Input
            id="tk"
            type="number"
            value={avgTicket}
            onChange={(e) => setAvgTicket(Number(e.target.value))}
            className="mt-1.5"
            data-testid="flyer-ticket"
          />
        </div>
        <div>
          <Label htmlFor="on" className="font-mono-label">Your Name</Label>
          <Input
            id="on"
            value={s?.owner_name || ""}
            onChange={(e) => saveOwner({ owner_name: e.target.value })}
            placeholder="James Ayers"
            className="mt-1.5"
            data-testid="flyer-owner-name"
          />
        </div>
        <div>
          <Label htmlFor="op" className="font-mono-label">Your Phone</Label>
          <Input
            id="op"
            value={s?.owner_phone || ""}
            onChange={(e) => saveOwner({ owner_phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="mt-1.5"
            data-testid="flyer-owner-phone"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="oe" className="font-mono-label">Your Email</Label>
          <Input
            id="oe"
            value={s?.owner_email || ""}
            onChange={(e) => saveOwner({ owner_email: e.target.value })}
            placeholder="jayers.me@gmail.com"
            className="mt-1.5"
            data-testid="flyer-owner-email"
          />
        </div>
      </div>

      {/* THE FLYER */}
      <div
        id="flyer"
        className="bg-white text-neutral-900 max-w-[8.5in] mx-auto shadow-2xl border border-border rounded-lg overflow-hidden"
        style={{ minHeight: "11in" }}
      >
        {/* Header band */}
        <div className="bg-navy text-white px-10 py-8 flex items-center gap-5">
          <img
            src={LOGO_URL}
            alt="Decline Drive"
            className="w-20 h-20 object-contain"
          />
          <div>
            <div className="font-display font-black text-4xl tracking-tighter">Decline Drive™</div>
            <div className="mt-1 text-[hsl(var(--gold-light))] font-mono-label text-xs">
              Automatic decline-service recovery for dealerships
            </div>
          </div>
        </div>

        {/* Gold stripe */}
        <div className="gold-divider" />

        {/* Hero pitch */}
        <div className="px-10 py-8">
          <div className="font-display text-3xl font-black tracking-tight leading-tight">
            Could you use{" "}
            <span className="text-[hsl(var(--gold))]">$150,000 additional</span>{" "}
            in your next 12 months?
            <br />
            We have the playbook.
          </div>
          <p className="mt-4 text-sm leading-relaxed text-neutral-700 max-w-2xl">
            The industry average is <b>4% follow-up</b> on declined recommendations. That&apos;s tens of
            thousands of dollars walking out your service drive every month. Decline Drive automatically texts
            every declined customer twice — {s?.stage_1_discount ?? 30}% off on day {s?.stage_1_days ?? 7},{" "}
            {s?.stage_2_discount ?? 40}% off on day {s?.stage_2_days ?? 15} — with a tap-to-book link and a
            unique coupon code. No advisor lifts a finger.
          </p>
        </div>

        {/* Features */}
        <div className="px-10 pb-6 grid grid-cols-2 gap-5">
          {[
            { Icon: Zap, title: "Fully automated", body: `Nightly cron in your quiet-hours window. Day ${s?.stage_1_days ?? 7} and Day ${s?.stage_2_days ?? 15} texts fire without a single click.` },
            { Icon: MessageSquare, title: "Editable wording", body: "Your dealership name, your voice, your discount %. Full template control." },
            { Icon: Calendar, title: "Customer books itself", body: "Tap-to-schedule from the text. Pick day, pick time, done — no phone tag." },
            { Icon: TrendingUp, title: "Per-advisor scorecards", body: "See who converts declines best. Coach the rest from real data." },
            { Icon: ShieldCheck, title: "Unique coupon codes", body: "DD-30-XXXXXX per customer — clean audit trail from text to redeemed RO." },
            { Icon: DollarSign, title: "Any DMS, any format", body: "CDK, Reynolds, Dealertrack, Tekion — upload nightly CSV, we handle the rest." },
          ].map(({ Icon, title, body }, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-10 h-10 rounded-md bg-navy text-white grid place-items-center shrink-0">
                <Icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold text-base">{title}</div>
                <div className="text-xs text-neutral-600 mt-0.5 leading-relaxed">{body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Industry benchmarks — the "sitting on your shelf" pitch */}
        <div className="mx-10 mb-2 rounded-lg bg-navy text-white p-5">
          <div className="font-mono-label text-[hsl(var(--gold-light))] mb-2">Industry Benchmarks</div>
          <div className="font-display text-lg font-black tracking-tight mb-3">
            The average franchised dealership leaves roughly{" "}
            <span className="text-[hsl(var(--gold))]">$100k – $400k</span> a year in service revenue on the shelf.
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div>
              <div className="font-mono-label text-[hsl(var(--gold-light))]">RO's / month</div>
              <div className="font-display text-lg font-bold text-white tabular-nums">400 – 1,500</div>
              <div className="text-white/60 text-[0.6rem] mt-0.5">Typical mid-size store</div>
            </div>
            <div>
              <div className="font-mono-label text-[hsl(var(--gold-light))]">Decline rate</div>
              <div className="font-display text-lg font-bold text-white tabular-nums">25 – 35%</div>
              <div className="text-white/60 text-[0.6rem] mt-0.5">Service dept. benchmarks</div>
            </div>
            <div>
              <div className="font-mono-label text-[hsl(var(--gold-light))]">Avg ticket</div>
              <div className="font-display text-lg font-bold text-white tabular-nums">$300 – $600</div>
              <div className="text-white/60 text-[0.6rem] mt-0.5">Recommended-service avg</div>
            </div>
          </div>
        </div>

        {/* ROI math */}
        <div className="mx-10 my-6 rounded-lg border-2 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5 p-6">
          <div className="font-mono-label text-neutral-700">Your recovery — real numbers, no guesses</div>
          <div className="mt-2 grid grid-cols-3 gap-4">
            <div>
              <div className="font-mono-label text-neutral-500">Weekly declines</div>
              <div className="font-display text-2xl font-bold">{weeklyDeclines}</div>
            </div>
            <div>
              <div className="font-mono-label text-neutral-500">Conversion</div>
              <div className="font-display text-2xl font-bold">{conversion}%</div>
            </div>
            <div>
              <div className="font-mono-label text-neutral-500">Avg RO</div>
              <div className="font-display text-2xl font-bold">{currency(avgTicket)}</div>
            </div>
          </div>
          <div className="gold-divider my-4" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-label text-neutral-500">Monthly recovered gross</div>
              <div className="font-display text-4xl font-black text-[hsl(var(--gold))] tracking-tighter">
                {currency(grossMonthly)}
              </div>
            </div>
            <div>
              <div className="font-mono-label text-neutral-500">Yearly recovered gross</div>
              <div className="font-display text-4xl font-black text-navy tracking-tighter">
                {currency(yearlyGross)}
              </div>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="mx-10 my-6 rounded-lg bg-navy text-white p-6">
          <div className="font-mono-label text-[hsl(var(--gold-light))]">Your investment</div>
          <div className="mt-2 grid grid-cols-3 gap-4 items-end">
            <div>
              <div className="font-display text-3xl font-black">{currency(s?.onboarding_fee || 895)}</div>
              <div className="text-xs text-white/70 mt-0.5">One-time onboarding</div>
            </div>
            <div>
              <div className="font-display text-3xl font-black">
                {currency(s?.monthly_fee || 1599)}<span className="text-lg">/mo</span>
              </div>
              <div className="text-xs text-white/70 mt-0.5">
                {s?.contract_months || 12}-month agreement
              </div>
            </div>
            <div>
              <div className="font-display text-lg font-bold text-[hsl(var(--gold-light))]">
                60-day guarantee
              </div>
              <div className="text-xs text-white/70 mt-0.5">Or full onboarding refund</div>
            </div>
          </div>
        </div>

        {/* Contact + LLC footer */}
        <div className="mx-10 mb-8 pt-6 border-t border-neutral-200 flex items-end justify-between gap-6">
          <div>
            <div className="font-mono-label text-neutral-500">Ready when you are</div>
            <div className="font-display text-lg font-bold mt-1">{s?.owner_name || "Your Name Here"}</div>
            <div className="text-sm text-neutral-700">{s?.owner_phone || "(555) 555-5555"}</div>
            <div className="text-sm text-neutral-700">{s?.owner_email || "you@example.com"}</div>
          </div>
          <div className="flex items-end gap-3">
            <DemoQRCode size={100} caption="Scan · try it live" />
            <div className="text-right">
              <img
                src={LOGO_URL}
                alt="Decline Drive"
                className="w-12 h-12 object-contain rounded ml-auto"
              />
              <div className="text-[0.6rem] text-neutral-500 mt-2 leading-tight">
                © {new Date().getFullYear()} Decline Drive™<br />
                Ayers Enterprises<br />
                All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
